import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {useSelector} from "react-redux";

import { EmailIcon, ImageIcon, PhoneIcon, StarIcon, UserIcon } from "../../assets/CustomIcon";
import { Modal, Form, Input, Select, Button, Flex, Upload, Typography, message } from "antd";
import { DeleteOutlined, LoadingOutlined } from "@ant-design/icons";

import { getBase64 } from "../../utils/appUtils";
import { VALIDATION_MESSAGES, CONTACT_TYPE_MAP } from "../../utils/constants";
import { 
    createContact,
    resetCreateContactStatus,
    selectCreateContactLoading, 
    selectCreateContactSuccess, 
    selectCreateContactError 
} from "../../redux/contactSlice";

const { Title } = Typography;

export default function ContactAddEdit(props) {
    const { openContact, setOpenContact, getListData, setGifLoader } = props;
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    // Redux Selectors
    const createLoading = useSelector(selectCreateContactLoading);
    const createSuccess = useSelector(selectCreateContactSuccess);
    const createError = useSelector(selectCreateContactError);

    // Local State
    const [uploadLoading, setUploadLoading] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);

    // Handle Create Success/Error
    useEffect(() => {
        if (createSuccess) {
            setTimeout(() => {
                setGifLoader(false);
                message.success("Contact created successfully!");
                dispatch(resetCreateContactStatus());
                form.resetFields();
                setImageUrl("");
                setOpenContact(false); // Close modal
                getListData();
            }, 5000);

        } else if (createError) {
            setGifLoader(false);
            message.error(createError || "Failed to create contact");
            dispatch(resetCreateContactStatus());
        }
    }, [createSuccess, createError, dispatch, form]);

    /**
     * Handler functions
     */
    const handleChange = (info) => {
        if (info.file.status === "uploading") {
            setUploadLoading(true);
            return;
        }
        // Antd Upload behavior can be tricky with controlled components
        console.log("Upload info:", info);

        // If beforeUpload returns false, file is in info.file (or info.fileList)
        // We already blocked upload, so we just want to preview
        const fileObj = info.file.originFileObj || info.file;
        if (fileObj) {
            getBase64(fileObj).then((url) => {
                setUploadLoading(false);
                setImageUrl(url);
                form.setFieldValue("avatar", fileObj);
            });
        }
    };

    const handleRemove = (e) => {
        e.stopPropagation();
        setImageUrl("");
        form.setFieldValue("avatar", null);
    };

    const normFile = (e) => {
        if (Array.isArray(e)) return e;
        return e?.fileList;
    };

    /**
     * Handles form submission.
     */
    const onFinish = async (values) => {
        setGifLoader(true);
        let mediaArray = [];
        const avatarFile = values.avatar;

        if (avatarFile) {
            const base64 = await getBase64(avatarFile);
            mediaArray = [
                {
                    file: base64,
                    file_name: avatarFile.name || 'existing-image',
                    file_extension: avatarFile.name ? avatarFile.name.split(".").pop() : "jpg",
                    file_location: "contact_image",
                    file_type: "CONTACT"
                },
            ];
        }

        const payload = {
            contact_type: CONTACT_TYPE_MAP[values.contactType] || 1,
            contact_name: values.contactName,
            email: values.emailID,
            mobile: values.phoneNumber,
            media: mediaArray,
        };
        dispatch(createContact(payload));
    };

    return (
        <Modal
            title={
                <Title level={3} className="modalTitle" strong>
                    Create New Contact
                </Title>
            }
            open={openContact}
            onCancel={() => setOpenContact(false)}
            footer={null}
            centered
            width={420}
            closeIcon
            className="createContactModal"
        >
            <Form layout="vertical" form={form} onFinish={onFinish}>
                <Form.Item
                    label=""
                    name="avatar"
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    className="uploadItem"
                >
                    <Flex justify="center">
                        <Upload
                            name="avatar"
                            listType="picture-circle"
                            className="avatar-uploader"
                            accept="image/*"
                            beforeUpload={(file) => {
                                const isImage = file.type.startsWith('image/');
                                if (!isImage) {
                                    message.error(`${file.name} is not a valid image file!`);
                                    return Upload.LIST_IGNORE;
                                }
                                const isLt2M = file.size / 1024 / 1024 < 2;
                                if (!isLt2M) {
                                    message.error("Image must be smaller than 2MB!");
                                    return Upload.LIST_IGNORE;
                                }
                                return false;
                            }}
                            showUploadList={false}
                            onChange={handleChange}
                            maxCount={1}
                        >
                            <div className="uploadImgContainer">
                                {imageUrl ? (
                                    <>
                                        <img src={imageUrl} alt="avatar" style={{ width: "100%", borderRadius: "50%" }} />
                                        <div className="avatarControls">
                                            <Button
                                                type="primary"
                                                shape="circle"
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={handleRemove}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className="uploadBtn">
                                        {uploadLoading ? <LoadingOutlined /> : <span className="uploadImgIocn"><ImageIcon /></span>}
                                        <div>Upload</div>
                                    </div>
                                )}
                            </div>
                        </Upload>
                    </Flex>
                </Form.Item>

                <Form.Item
                    label=""
                    name="contactName"
                    rules={[{ required: true, message: VALIDATION_MESSAGES.NAME_REQUIRED }]}
                >
                    <Input prefix={<UserIcon />} placeholder="Full Name" size="large" />
                </Form.Item>

                <Form.Item
                    label=""
                    name="emailID"
                    rules={[
                        { required: true, message: "Email ID is required" },
                        { type: "email", message: VALIDATION_MESSAGES.EMAIL_INVALID },
                    ]}
                >
                    <Input prefix={<EmailIcon />} placeholder="Email ID" size="large" />
                </Form.Item>

                <Form.Item
                    label=""
                    name="phoneNumber"
                    rules={[
                        { required: true, message: VALIDATION_MESSAGES.PHONE_REQUIRED || "Phone Number is required" },
                        { pattern: /^[0-9]{10}$/, message: VALIDATION_MESSAGES.PHONE_INVALID },
                    ]}
                >
                    <Input prefix={<PhoneIcon />} placeholder="Phone Number" size="large" />
                </Form.Item>

                <Form.Item
                    label=""
                    name="contactType"
                    rules={[{ required: true, message: VALIDATION_MESSAGES.CONTACT_TYPE_REQUIRED }]}
                >
                    <Select size="large" placeholder="Contact Type" prefix={<StarIcon />}>
                        <Select.Option value="Friend">Friend</Select.Option>
                        <Select.Option value="Family">Family</Select.Option>
                        <Select.Option value="Work">Work</Select.Option>
                    </Select>
                </Form.Item>

                <Flex justify="center" className="modalBtnWrap">
                    <Button type="primary" size="large" className="btnGradient publishBtn" htmlType="submit" loading={createLoading}>
                        Save and Update
                    </Button>
                </Flex>
            </Form>
        </Modal>
    );
}